// Adapts the new graphql-gateway response shape to the shape expected by
// web-shared-components portfolio templates (Adams / Moodie / Newton),
// which were built against the legacy portfolios-api schema:
//   Photo.name           -> now Photo.title
//   Photo.legacyId       -> removed (derived from the global id when possible)
//   Photo.images(sizes)  -> now Photo.urls { small medium large }
//   PhotoConnection.totalCount -> removed (derived from edges length)

// Legacy 500px resize-size codes requested by the templates, mapped to the
// closest of the three urls the new gateway exposes.
const PHOTO_SIZE_URL_KEYS = {
  32: 'small',
  33: 'medium',
  34: 'medium',
  35: 'large',
  36: 'large',
};

const COVER_SIZE_URL_KEYS = {
  21: 'medium',
  35: 'large',
};

const decodeLegacyId = (globalId) => {
  if (!globalId) return globalId;
  try {
    const decoded = window.atob(globalId);
    const parts = decoded.split(':');
    return parts[parts.length - 1] || globalId;
  } catch (e) {
    return globalId;
  }
};

const toImages = (urls, sizeUrlKeys) => {
  if (!urls) return [];
  return Object.keys(sizeUrlKeys)
    .map((size) => {
      const url = urls[sizeUrlKeys[size]];
      return {
        size: Number(size),
        url,
        jpegUrl: url,
        webpUrl: url,
      };
    })
    .filter(image => image.url);
};

const adaptPhoto = (photo, sizeUrlKeys) => {
  if (!photo) return photo;
  return {
    ...photo,
    legacyId: decodeLegacyId(photo.id),
    name: photo.title,
    images: toImages(photo.urls, sizeUrlKeys),
  };
};

const adaptCoverPhoto = (coverPhoto) => {
  if (!coverPhoto) return coverPhoto;
  // PortfolioCoverPhoto variant: getCoverPhotoUrl in the templates only knows
  // coverPhoto.avatar.images / coverPhoto.images, so the photo urls must also
  // be exposed as a top-level images array
  if (coverPhoto.photo) {
    const images = coverPhoto.photo.urls ? [{ url: coverPhoto.photo.urls.large }] : [];
    return {
      ...coverPhoto,
      images,
      photo: {
        ...coverPhoto.photo,
        images,
      },
    };
  }
  // PortfolioCoverImage / PortfolioCoverAvatar keep the legacy shape
  return coverPhoto;
};

const adaptFolder = (folder) => {
  if (!folder) return folder;
  const edges = folder.photos?.edges || [];
  return {
    ...folder,
    coverPhoto: adaptPhoto(folder.coverPhoto, COVER_SIZE_URL_KEYS),
    photos: folder.photos && {
      ...folder.photos,
      edges: edges.map(edge => edge && ({
        ...edge,
        node: adaptPhoto(edge.node, PHOTO_SIZE_URL_KEYS),
      })),
      totalCount: edges.length,
    },
  };
};

const adaptPortfolio = (portfolio) => {
  if (!portfolio) return portfolio;
  return {
    ...portfolio,
    coverPhoto: adaptCoverPhoto(portfolio.coverPhoto),
    folders: (portfolio.folders || []).map(adaptFolder),
  };
};

export default adaptPortfolio;
